import { Context, Data, Effect, flow, Layer } from 'effect';
import { FetchHttpClient, HttpClient, HttpClientError, HttpClientRequest } from 'effect/http';
import { type AccountError, AccountService } from './account';
import { type AuthTokenError, AuthTokenService } from './auth-token';
import { EnvironmentService } from './environment';
import { type NetworkError, withTransportConventions } from './http-conventions';
import { FORBIDDEN_STATUS, UNAUTHORIZED_STATUS } from './http-status';

/** Header the API identifies the acting account by. */
const ACCOUNT_ID_HEADER = 'x-account-id';

/**
 * Raised when the API rejects the bearer token the request carried.
 *
 * The counterpart to `MissingAuthTokenError`: that one means no token was available to send, this one means a token was sent and refused. Both end with the user going back through auth, but only this one proves the token is actually stale rather than absent.
 */
export class AuthenticationError extends Data.TaggedError('AuthenticationError')<{
	readonly method: HttpClientRequest.HttpClientRequest['method'];
	readonly url: string;
}> {}

/**
 * Raised when the API accepts the bearer token but denies access to the resource.
 *
 * Distinct from {@link AuthenticationError} because the remedy differs: signing in again changes nothing here. The user is who they claim to be and still may not have this. Callers should say so rather than bouncing them through auth.
 */
export class AuthorizationError extends Data.TaggedError('AuthorizationError')<{
	readonly method: HttpClientRequest.HttpClientRequest['method'];
	readonly url: string;
}> {}

/** Everything a call made through {@link ApiClient} can fail with. */
export type ApiClientError = AccountError | AuthTokenError | AuthenticationError | AuthorizationError | NetworkError | HttpClientError.HttpClientError;

/**
 * An `HttpClient` preconfigured for the Acies Core API.
 *
 * Requests are made with paths relative to the environment's API origin, so callers never spell out a host: `client.get('/configurator/apps/one/stores')`.
 */
export class ApiClient extends Context.Service<ApiClient, HttpClient.HttpClient.With<ApiClientError>>()('@acies/platform/ApiClient', {
	/** Wraps whichever `HttpClient` is in context with this API's conventions. */
	make: Effect.gen(function* () {
		const { apiUrl } = yield* EnvironmentService;
		const { token } = yield* AuthTokenService;
		const { account } = yield* AccountService;
		const httpClient = yield* HttpClient.HttpClient;
		return httpClient.pipe(
			HttpClient.mapRequest(flow(HttpClientRequest.prependUrl(apiUrl), HttpClientRequest.acceptJson)),
			// Effectful, because both the token and the account are read per
			// request rather than captured once when this layer is built: the
			// host app refreshes one and rewrites the other mid-session.
			// Retries re-read them too, since retrying wraps the request
			// pipeline as a whole.
			HttpClient.mapRequestEffect((request) =>
				Effect.gen(function* () {
					const bearer = yield* token;
					const { id } = yield* account;
					return request.pipe(HttpClientRequest.bearerToken(bearer), HttpClientRequest.setHeader(ACCOUNT_ID_HEADER, id));
				}),
			),
			// Both before the conventions below, so 401 and 403 never become a
			// StatusCodeError: callers catch them by tag instead of reaching
			// into `reason.response.status` on an HttpClientError. Every other
			// non-2xx status falls through to filterStatusOk unchanged.
			HttpClient.filterOrFail(
				(response) => response.status !== UNAUTHORIZED_STATUS,
				(response) =>
					new AuthenticationError({
						method: response.request.method,
						url: response.request.url,
					}),
			),
			HttpClient.filterOrFail(
				(response) => response.status !== FORBIDDEN_STATUS,
				(response) =>
					new AuthorizationError({
						method: response.request.method,
						url: response.request.url,
					}),
			),
			// Last, so everything above is what this API needs and everything the
			// exchange itself needs is in one shared place. See withTransportConventions
			// for why the retry, the NetworkError and the tracer reference sit in
			// that order.
			withTransportConventions,
		);
	}),
}) {
	/** The client itself, leaving its dependencies to the caller. */
	static readonly layerNoDeps: Layer.Layer<ApiClient, never, AccountService | AuthTokenService | EnvironmentService | HttpClient.HttpClient> = Layer.effect(this)(this.make);

	/** The client wired to the browser: fetch, hostname detection, session storage. */
	static readonly layer: Layer.Layer<ApiClient> = ApiClient.layerNoDeps.pipe(Layer.provide(FetchHttpClient.layer), Layer.provide(EnvironmentService.layer), Layer.provide(AuthTokenService.layer), Layer.provide(AccountService.layer));
}
